import { createFileRoute, useNavigate } from '@tanstack/react-router';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useMemo, useState } from 'react';

import ActionButton from '@/components/Button/ActionButton';
import { AttributeCard } from '@/components/Card/AttributeCard';
import Icon from '@/components/Icon/Icon';
import type { IconName } from '@/components/Icon/iconMap';
import { Grid, Spacer, VStack } from '@/components/Layout';
import StepperHeaderComponent from '@/components/StepperHeader';
import { Text } from '@/components/Typography/Text';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { DeviceType } from '@/constants/device';
import { useDeviceType } from '@/features/device/device-type/useDeviceType';
import { defaultNS, useTranslation } from '@/lib/i18n';

import { AddCatalogStepKeys } from './-constants/constant';

export const Route = createFileRoute('/_staff/device-catalog/add/')({
  component: Introduce,
});

type CommonProps = {
  selectedDeviceType: DeviceType | null;
  setSelectedDeviceType: Dispatch<SetStateAction<DeviceType | null>>;
};

function Introduce() {
  const [selectedDeviceType, setSelectedDeviceType] =
    useState<DeviceType | null>(DeviceType.COMPUTER);

  return (
    <VStack className="mx-auto w-[1000px] ">
      <Header />
      <Spacer variant="xs" />
      <DeviceTypeRadioGroup
        selectedDeviceType={selectedDeviceType}
        setSelectedDeviceType={setSelectedDeviceType}
      />
    </VStack>
  );
}

function Header() {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'pages.deviceCatalog.add.intro.header',
  });

  return (
    <VStack>
      <Spacer variant="xxl" />
      <StepperHeaderComponent headerTitle={t('headerTitle')} />
    </VStack>
  );
}

function DeviceTypeRadioGroup({
  selectedDeviceType,
  setSelectedDeviceType,
}: CommonProps) {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'pages.deviceCatalog.add.intro.body',
  });
  const { t: tDeviceType } = useDeviceType();

  const DEVICE_TYPES: {
    id: DeviceType;
    title: string;
    requiredDetails: { iconName: IconName; text: string }[];
  }[] = useMemo(
    () => [
      {
        id: DeviceType.COMPUTER,
        title: tDeviceType('computer') as string,
        requiredDetails: [
          { iconName: 'SquareDot', text: t('itemCard.deviceCategory') },
          { iconName: 'SquareDot', text: t('itemCard.catalogName') },
        ],
      },
      {
        id: DeviceType.MOBILE,
        title: tDeviceType('mobile') as string,
        requiredDetails: [
          { iconName: 'SquareDot', text: t('itemCard.deviceCategory') },
          { iconName: 'SquareDot', text: t('itemCard.catalogName') },
        ],
      },
      {
        id: DeviceType.MONITOR,
        title: tDeviceType('monitor') as string,
        requiredDetails: [
          { iconName: 'SquareDot', text: t('itemCard.catalogName') },
        ],
      },
      {
        id: DeviceType.SERVER,
        title: tDeviceType('server') as string,
        requiredDetails: [
          { iconName: 'SquareDot', text: t('itemCard.catalogName') },
        ],
      },
      {
        id: DeviceType.OTHER,
        title: tDeviceType('other') as string,
        requiredDetails: [
          { iconName: 'SquareDot', text: t('itemCard.catalogName') },
        ],
      },
    ],
    [t, tDeviceType],
  );

  const navigate = useNavigate();

  const handleRadioChange = useCallback(
    (value: DeviceType) => {
      setSelectedDeviceType(value);
    },
    [setSelectedDeviceType],
  );

  const handleButtonClick = useCallback(() => {
    if (selectedDeviceType) {
      navigate({
        to: `/device-catalog/add/${selectedDeviceType}`,
        hash: AddCatalogStepKeys.BASIC,
      });
    }
  }, [selectedDeviceType, navigate]);

  return (
    <>
      <Spacer variant="xl" />
      <Text variant="h4"> {t('selectDeviceType')}</Text>
      <Spacer variant="l" />
      <RadioGroup onValueChange={handleRadioChange}>
        <Grid columns={2} rowGap={28} columnGap={24}>
          {DEVICE_TYPES.map((type) => (
            <AttributeCard
              key={type.id}
              title={type.title}
              titleStyle="CardHeader"
              onClick={() => handleRadioChange(type.id)}
              actionButton={
                <RadioGroupItem
                  id={type.id}
                  value={type.id}
                  className="h-4 w-4"
                  checked={selectedDeviceType === type.id}
                />
              }
              actionButtonPosition="top-left"
              actionButtonIsInner={false}
              className="cursor-pointer"
            >
              <VStack className="gap-3">
                <Text variant="labelSmall">
                  {t('itemCard.requiredDetails')}
                </Text>
                {type.requiredDetails.map((detail, index) => (
                  <Icon
                    key={`${detail.text}-${index}`}
                    iconName={detail.iconName}
                    text={detail.text}
                  />
                ))}
              </VStack>
            </AttributeCard>
          ))}
        </Grid>
      </RadioGroup>
      <Spacer variant="xl" />
      <ActionButton
        icon="FilePenIcon"
        className="w-48"
        onClick={handleButtonClick}
      >
        {t('fillOutDetails')}
      </ActionButton>
    </>
  );
}
